import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detectar si es iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Verificar si la app ya está instalada
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Escuchar el evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Mostrar el prompt después de un delay para no ser intrusivo
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Escuchar cuando la app es instalada
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Para iOS, mostrar instrucciones después de un tiempo
    if (isIOSDevice) {
      const hasSeenIOSPrompt = localStorage.getItem('ios-pwa-prompt');
      if (!hasSeenIOSPrompt) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      // Guardar que el usuario vio el prompt de iOS
      localStorage.setItem('ios-pwa-prompt', 'true');
      setShowPrompt(false);
      return;
    }

    if (!deferredPrompt) return;

    // Mostrar el prompt de instalación nativo
    deferredPrompt.prompt();

    // Esperar la respuesta del usuario
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('Usuario aceptó instalar la PWA');
    } else {
      console.log('Usuario rechazó instalar la PWA');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Guardar en localStorage para no mostrar por un tiempo
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // No mostrar si ya está instalada
  if (isInstalled) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          <div className="bg-gradient-to-br from-purple-900 to-purple-950 border border-purple-500/30 rounded-2xl p-5 shadow-2xl shadow-purple-500/20">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-300" />
                </div>
                <div>
                  <h3 className="font-bold text-purple-100">Instala Trismosim</h3>
                  <p className="text-sm text-purple-200/60">Acceso rápido a tu portal</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 rounded-full text-purple-300 hover:text-purple-100 hover:bg-purple-500/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="mb-4">
              {isIOS ? (
                <div className="text-sm text-purple-200/80 space-y-2">
                  <p>Para instalar en iOS:</p>
                  <ol className="list-decimal list-inside space-y-1 text-purple-200/60">
                    <li>Toca el botón <strong>Compartir</strong> en Safari</li>
                    <li>Desplaza y selecciona <strong>"Agregar a Inicio"</strong></li>
                  </ol>
                  <div className="flex items-center gap-2 mt-3 text-purple-300">
                    <Smartphone className="w-4 h-4" />
                    <span className="text-xs">Funciona sin conexión</span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-purple-200/80 space-y-2">
                  <p>Instala Trismosim en tu dispositivo para:</p>
                  <ul className="space-y-1 text-purple-200/60">
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">✦</span>
                      Acceso rápido desde tu pantalla de inicio
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">✦</span>
                      Funciona sin conexión a internet
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">✦</span>
                      Notificaciones de lunaciones y eventos
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Actions */}
            {!isIOS && (
              <Button
                onClick={handleInstall}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white"
              >
                <Download className="w-5 h-5 mr-2" />
                Instalar Aplicación
              </Button>
            )}

            {isIOS && (
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="w-full border-purple-400/30 text-purple-200 hover:bg-purple-500/10"
              >
                Entendido
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
